import React, {useEffect, useState} from 'react';
import { styled } from '@mui/material/styles';
import {
  AppBar,
  Typography,
  Paper,
  TableCell,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  TextField,
  FormLabel,
  Tooltip,
  CircularProgress,
  Backdrop,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import {Auth} from '../../Api/auth';
import UserTransferList from '../Administrator/UserTransferList';
import {DeleteProjectDialog} from './AlertDialogs';
import EnhancedTable from './EnhancedTable';
import Snackbar from '../CommonComponents/Snackbar';

import { useMsal } from "@azure/msal-react";

const PREFIX = 'ProjectsList';

const classes = {
  root: `${PREFIX}-root`,
  vettingContainerTitle: `${PREFIX}-vettingContainerTitle`,
  vettingSection: `${PREFIX}-vettingSection`,
  vettingRow: `${PREFIX}-vettingRow`,
  vettingColumn: `${PREFIX}-vettingColumn`,
  dialogFooter: `${PREFIX}-dialogFooter`,
  table: `${PREFIX}-table`,
  button: `${PREFIX}-button`,
  appBarHeader: `${PREFIX}-appBarHeader`,
  backdrop: `${PREFIX}-backdrop`,
  paper: `${PREFIX}-paper`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.root}`]: {
    '& .MuiDialogTitle-root': {
      padding: theme.spacing(1.5, 3),
    },
  },

  [`& .${classes.vettingContainerTitle}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  [`& .${classes.vettingSection}`]: {
    display: 'flex',
    flexFlow: 'column',
    padding: theme.spacing(3),
    overflowY: 'auto',
  },

  [`& .${classes.vettingRow}`]: {
    'display': 'flex',
    'margin': theme.spacing(1.5, 0),
    'flexFlow': 'row',
    'height': '100%',
    'justifyContent': 'center',
    'width': '100%',
    'alignItems': 'center',
    '&:first-child': {
      marginTop: 0,
    },
    '&:last-child': {
      marginBottom: 0,
    },
  },

  [`& .${classes.vettingColumn}`]: {
    'display': 'flex',
    'flexDirection': 'column',
    'width': '100%',
    'justifyContent': 'center',
    'marginRight': theme.spacing(1),
    'height': '100%',
    '&:last-child': {
      marginRight: 0,
    },
  },

  [`& .${classes.dialogFooter}`]: {
    padding: theme.spacing(1.75, 3),
    borderTop: '1px solid',
    borderTopColor: theme.palette.divider,
  },

  [`& .${classes.table}`]: {
    minWidth: 650,
  },

  [`& .${classes.button}`]: {
    width: '25%',
  },

  [`& .${classes.appBarHeader}`]: {
    color: theme.palette.text.primary,
    background: 'white',
    position: 'static',
    top: 0,
    left: 'auto',
    width: '100%',
    padding: theme.spacing(2, 1, 1),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  [`& .${classes.backdrop}`]: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },

  [`& .${classes.paper}`]: {
    width: 380,
    maxHeight: 230,
    overflow: 'auto',
  }
}));

export default function ProjectsList(){

  
  const [open, setOpen] = useState({flag: false, deleteFlag: false, id: 0});
  const defaultFormVals={
    name:"", 
    organizationAbbreviation:"", 
    projectNumber:"", 
    contractStartYear:"", 
    contractNumber:"", 
    users:[],
    requesters:[],
    organizationAbbreviationError:"", 
    projectNumberError:"", 
    contractStartYearError:"", 
    contractNumberError:"",
    researcherWorkspaceId:""
  }
  const [formValues, setFormValues] = useState(defaultFormVals);
  const [rows, setRows] = useState([]);
  const [userList, setUserList] = useState({});
  const [snackbarState, setSnackbarState] = React.useState({
    open: false,
    severity: "success",
    message: ""
  });
  const [projectHistory, setProjectHistory] = React.useState({
    open: false,
    data: [],
  });
  const [backdropOpen, setBackdropOpen] = React.useState(false);

  const handleClose = () => {
    let temp={...open}
    temp.flag=false
    setOpen(temp);
  };

  const handleValueChange = (e) => {
    let temp = {...formValues}
    temp[e.target.name] = e.target.value;
    setFormValues(temp);
  };

  const trimFormValues=()=>{
    return {...formValues, 
      name:formValues.name.trim(), 
      projectNumber: formValues.projectNumber.trim(),
      organizationAbbreviation:formValues.organizationAbbreviation.trim(), 
      contractNumber:formValues.contractNumber.trim()
    };
  };

  const {instance}=useMsal();

  useEffect(()=> {
    setBackdropOpen(true);
    Auth.authorizedFetch("/api/initManageUsers",{},instance)
    .then((response)=> {
      if(!response.ok){
        setSnackbarState({open: true, severity: "error", message: response.statusText});
        throw Error(response.statusText)
      }
      
      return response.json();
    })
    .then(data => {
      setRows(data.projects);
      
      let temp = {};
      for(let user in data.users){
        temp[data.users[user].id] = data.users[user];
      }
      setUserList(temp);
    })
    .finally(()=>{
      setBackdropOpen(false);
    });
  }, []);

  const getProjects=()=>{
    setBackdropOpen(true);
    Auth.authorizedFetch("/api/getProjects",{},instance)
    .then((response)=> {
      if(!response.ok){
        setSnackbarState({open: true, severity: "error", message: response.statusText});
        throw Error(response.statusText)
      }
      
      return response.json();
    })
    .then(data => {
      setRows(data.data);
    })
    .finally(()=>{
      setBackdropOpen(false);
    });
  };

  const validateForm=(formValues)=>{
    let errFlag=false;
    let missingMessage="Missing value";
    let errors={
      organizationAbbreviationError:"", 
      projectNumberError:"", 
      contractStartYearError:"", 
      contractNumberError:""
    }
    if(formValues.organizationAbbreviation===""){
      errors.organizationAbbreviationError=missingMessage;
      errFlag=true;
    }
    if(formValues.projectNumber===""){
      errors.projectNumberError=missingMessage;
      errFlag=true;
    }
    if(formValues.contractStartYear===""){
      errors.contractStartYearError=missingMessage;
      errFlag=true;
    }
    if(formValues.contractNumber===""){
      errors.contractNumberError=missingMessage;
      errFlag=true;
    }
    if(errFlag){
      setFormValues({...formValues, ...errors});
    }
    return errFlag
  }

  const addNewProject = ()=>{
    let trimmedValues = trimFormValues();
    if(validateForm(trimmedValues)){
      return;
    }
    Auth.authorizedFetch("/api/createNewProject", {method:"POST", body: JSON.stringify(trimmedValues), headers:{'Content-type':'application/json'}},instance)
    .then((response)=> {
      if(!response.ok){
        setSnackbarState({open: true, severity: "error", message: response.statusText});
        throw Error(response.statusText)
      }
      setSnackbarState({open: true, severity: "success", message: "New project created!"});
      getProjects();
      handleClose();
    });
  }

  const handleNewOpen = () => {
    setOpen({...open, flag: true, id:0});
    setFormValues(defaultFormVals);
  };

  const editProject = (id)=>{
    let trimmedValues = trimFormValues();
    if(validateForm(trimmedValues)){
      return;
    }
    trimmedValues.id=id;
    Auth.authorizedFetch("/api/editProject", {method:"POST", body: JSON.stringify(trimmedValues), headers:{'Content-type':'application/json'}},instance)
    .then((response)=> {
      if(!response.ok){
        setSnackbarState({open: true, severity: "error", message: response.statusText});
        throw Error(response.statusText)
      }
      setSnackbarState({open: true, severity: "success", message: "Project edited!"});
      getProjects();
      handleClose();
    });
  }

  const handleEditOpen = (id) => {
    rows.some((row)=> {
      if(row.id===id){
        setFormValues({...defaultFormVals,
          name:row.name,
          organizationAbbreviation:row.organizationAbbreviation,
          projectNumber:row.projectNumber,
          contractStartYear:row.contractStartYear,
          contractNumber: row.contractNumber,
          researcherWorkspaceId: row.researcherWorkspaceId,
          users:row.users,
          requesters: row.requesters
        });
        setOpen({...open, flag:true, id: id});
        return true;
      }
    });
  };

  const handleGetHistory=(id)=>{
    Auth.authorizedFetch("/api/getProjectHistory/"+id, {method:"GET"},instance)
    .then((response)=> {
      if(!response.ok){
        setSnackbarState({open: true, severity: "error", message: response.statusText});
        throw Error(response.statusText)
      }
      
      return response.json();
    })
    .then(data => {
      console.log(data.data);
      setProjectHistory({
        open: true,
        data: data.data,
      });
    });
  }

  const deleteProject = (id)=>{
    Auth.authorizedFetch("/api/deleteProject", {method:"POST", body:JSON.stringify({id:id})},instance)
    .then((response)=> {
      if(response.status===405){
        response.text().then((text)=>{
          setSnackbarState({open: true, severity: "error", message: text});
        });
      }else if(!response.ok){
        setSnackbarState({open: true, severity: "error", message: response.statusText});
        throw Error(response.statusText)
      }
      setSnackbarState({open: true, severity: "success", message: "Project deleted!"});
      getProjects();
    });
  }

  const projectUsersList=(row)=>{
    let list = [];
    row.users.map(k=>{
      list.push(userList[k]);
    });
    list.sort((a,b)=>{
      a=a.userAccount.toLowerCase();
      b=b.userAccount.toLowerCase();
      return a<b ? -1 : a>b ? 1: 0;
    });
    return (
      <Paper className={classes.paper}>
        <ul style={{paddingLeft: 20}}>
          {list.map((user, i)=>
            <li key={i}>
              {user.userAccount}
              {row.requesters.includes(user.id) &&
                <Tooltip arrow title="This user has a request with this project">
                  <InfoIcon style={{position: 'relative', top: '6px'}}/>
                </Tooltip>
              }
            </li>
          )}
        </ul>
      </Paper>
    );
  };

  const headCells = [
    {id: 'name', label: "Project Name", isOrderable: true},
    {id: 'organizationAbbreviation', label: "Organization Abbreviation", isOrderable: true},
    {id: 'projectNumber', label: "Project Number", isOrderable: true},
    {id: 'contractStartYear', label: "Contract Start Year", isOrderable: true},
    {id: 'contractNumber', label: "Contract Number", isOrderable: true},
    {id: 'researcherWorkspaceId', label: "Researcher Workspace", isOrderable: false},
    {id: 'users', label: "Project Users", isOrderable: true},
    {id: 'actions', label: "Actions", isOrderable: false}
  ];

  const getTableRow=(row)=>{
    return (
      <TableRow key={row.id}>
        <TableCell component="th" scope="row">{row.name}</TableCell>
        <TableCell>{row.organizationAbbreviation}</TableCell>
        <TableCell>{row.projectNumber}</TableCell>
        <TableCell>{row.contractStartYear}</TableCell>
        <TableCell>{row.contractNumber}</TableCell>
        <TableCell>{row.researcherWorkspaceId}</TableCell>
        <TableCell>
          {Object.keys(userList).length!==0 &&
            projectUsersList(row)
          }
        </TableCell>
        <TableCell>
          <Button onClick={()=>handleGetHistory(row.id)}>View History</Button>
          <Button onClick={()=>handleEditOpen(row.id)}>Modify</Button>
          {row.requesters.length===0 ? 
            <Button onClick={()=>setOpen({...open, deleteFlag: true, id:row.id})}>Delete</Button>
          :
            <Tooltip title="A project can't be deleted if it has requests associated to it">
              <span><Button disabled>Delete</Button></span>
            </Tooltip>
          }
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Root>
      <AppBar
        className={classes.appBarHeader}
        elevation={0}
      >
        <Typography
          variant="h6"
          component="h1"
        >
          Manage projects
        </Typography>
      </AppBar>
      <Button className={classes.button} variant="outlined" onClick={()=>handleNewOpen()}>Add new project</Button>
      <EnhancedTable
        headCells={headCells}
        rows={rows}
        getTableRow={getTableRow}
      />
      <Dialog
        open={open.flag}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.root}
        maxWidth={"lg"}
        fullWidth={true}
      >
        <DialogTitle id="alert-dialog-title" className={classes.vettingContainerTitle}>
          <Typography variant="h6" component="h2">
            {open.id === 0 ? "Add new Project" : "Modify existing Project"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div className={classes.vettingSection}>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <TextField
                  autoFocus
                  id="name"
                  name="name"
                  label="Project Name"
                  sx={{mt: 1}}
                  type="text"
                  fullWidth
                  onChange={handleValueChange}
                  value={formValues.name}
                />
              </div>
              <div className={classes.vettingColumn}>
                <TextField
                  id="organizationAbbreviation"
                  name="organizationAbbreviation"
                  label="Organization Abbreviation"
                  sx={{mt: 1}}
                  type="text"
                  fullWidth
                  required
                  onChange={handleValueChange}
                  value={formValues.organizationAbbreviation}
                  error={Boolean(formValues.organizationAbbreviationError)}
                  helperText={formValues.organizationAbbreviationError}
                />
              </div>
              <div className={classes.vettingColumn}>
                <TextField
                  id="projectNumber"
                  name="projectNumber"
                  label="Project Number"
                  sx={{mt: 1}}
                  type="text"
                  fullWidth
                  required
                  onChange={handleValueChange}
                  value={formValues.projectNumber}
                  error={Boolean(formValues.projectNumberError)}
                  helperText={formValues.projectNumberError}
                />
              </div>
            </div>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <TextField
                  id="contractStartYear"
                  name="contractStartYear"
                  label="Contract Start Year"
                  sx={{mt: 1}}
                  type="number"
                  fullWidth
                  required
                  onChange={handleValueChange}
                  value={formValues.contractStartYear}
                  error={Boolean(formValues.contractStartYearError)}
                  helperText={formValues.contractStartYearError}
                />
              </div>
              <div className={classes.vettingColumn}>
                <TextField
                  id="contractNumber"
                  name="contractNumber"
                  label="Contract Number"
                  sx={{mt: 1}}
                  type="text"
                  fullWidth
                  required
                  onChange={handleValueChange}
                  value={formValues.contractNumber}
                  error={Boolean(formValues.contractNumberError)}
                  helperText={formValues.contractNumberError}
                />
              </div>
              <div className={classes.vettingColumn}>
                <InputLabel id="researcherWorkspaceId-label">Researcher workspace id</InputLabel>
                  <Select 
                  id="researcherWorkspaceId"
                  name="researcherWorkspaceId"
                  labelId="researcherWorkspaceId-label"
                  label="Researcher workspace id"
                  value={formValues.researcherWorkspaceId}
                  onChange={handleValueChange}
                  >
                    <MenuItem key={1} value={1}>1</MenuItem>
                    <MenuItem key={2} value={2}>2</MenuItem>
                  </Select>
            </div>
            </div>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <FormLabel sx={{mt: 1}}>Users</FormLabel>
                {Object.keys(userList).length!==0 && 
                  <UserTransferList 
                    formValues={formValues}
                    setFormValues={setFormValues}
                    userList={userList}
                  />
                }
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogFooter}>
          <Button variant="outlined" onClick={handleClose}>Cancel</Button>
          {open.id === 0 &&
            <Button variant="outlined" onClick={addNewProject}>Add new project</Button>
          }
          {open.id !== 0 &&
            <Button variant="outlined" onClick={()=>editProject(open.id)}>Modify Project</Button>
          }
        </DialogActions>
      </Dialog>
      <Dialog
        open={projectHistory.open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.root}
        maxWidth={"lg"}
        fullWidth={true}
        onClose={()=>{
          setProjectHistory({
            open: false,
            data: [],
          })
        }}
        scroll="paper"
      >
        <DialogContent dividers={true}>
          <DialogContentText color="textPrimary">
            <ul>
              {projectHistory.data.map((hist, i)=>
                <li key={i}>[{hist.dateCreated}] {hist.user}({hist.role}) - {hist.userProjectAction}</li>
              )}
            </ul>
          </DialogContentText>
        </DialogContent>
      </Dialog>
      <Backdrop className={classes.backdrop} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <DeleteProjectDialog 
        toggleDialog={()=>setOpen({...open, deleteFlag: false, id:0})}
        handleDelete={deleteProject}
        open={open.deleteFlag}
        projectId={open.id}
      />
      <Snackbar 
        message={snackbarState.message}
        severity={snackbarState.severity}
        open={snackbarState.open}
        handleClose={()=>{setSnackbarState({...snackbarState, open: false, message: ""})}}
      />
    </Root>
  );
}
